import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import {
  Plus, Receipt, TrendingUp, TrendingDown, RussianRuble, Edit, Trash2, CheckCircle,
  ChevronLeft, ChevronRight, Wallet, Upload, AlertTriangle,
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter,
  startOfYear, endOfYear, addWeeks, addMonths, addQuarters, addYears, format,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { DataTable } from '../components/ui/data-table';
import { LoadingCard } from '../components/ui/loading-spinner';
import { StatsCard } from '../components/ui/stats-card';
import { expensesApi, billingApi } from '../lib/api';
import { formatDate, formatCurrency, getStatusBadgeClass, getStatusLabel, getErrorMessage } from '../lib/utils';
import { toast } from 'sonner';
import ExpenseDialog from '../components/ExpenseDialog';
import UploadResultsDialog from '../components/UploadResultsDialog';

// Календарные границы периода (а не "N дней от сейчас") — чтобы можно было
// листать в прошлое и увидеть, например, ровно май, а не последние 30 дней.
const PERIOD_CONFIG = {
  week: { start: (d) => startOfWeek(d, { weekStartsOn: 1 }), end: (d) => endOfWeek(d, { weekStartsOn: 1 }), shift: addWeeks },
  month: { start: startOfMonth, end: endOfMonth, shift: addMonths },
  quarter: { start: startOfQuarter, end: endOfQuarter, shift: addQuarters },
  year: { start: startOfYear, end: endOfYear, shift: addYears },
};

function getRange(period, anchor) {
  const cfg = PERIOD_CONFIG[period];
  return { start: cfg.start(anchor), end: cfg.end(anchor) };
}

function formatRangeLabel(period, start, end) {
  if (period === 'year') return format(start, 'yyyy');
  if (period === 'month') {
    const label = format(start, 'LLLL yyyy', { locale: ru });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  if (period === 'quarter') {
    const q = Math.floor(start.getMonth() / 3) + 1;
    return `${['I', 'II', 'III', 'IV'][q - 1]} квартал ${format(start, 'yyyy')}`;
  }
  return start.getMonth() === end.getMonth()
    ? `${format(start, 'd')}–${format(end, 'd MMMM yyyy', { locale: ru })}`
    : `${format(start, 'd MMMM', { locale: ru })} – ${format(end, 'd MMMM yyyy', { locale: ru })}`;
}

export default function Expenses() {
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState('');
  const [period, setPeriod] = useState('month');
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  // Тарифный флаг берём из /billing/subscription — единственный источник
  // правды, тот же паттерн, что у загрузки PDF счетов/платёжек в Invoices.jsx.
  const { data: subscriptionData } = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: () => billingApi.getSubscription().then((res) => res.data),
  });
  const hasDocRecognition = !!subscriptionData?.subscription?.has_document_recognition;

  const { start: rangeStart, end: rangeEnd } = useMemo(() => getRange(period, anchorDate), [period, anchorDate]);
  const dateParams = useMemo(() => ({
    start_date: format(rangeStart, 'yyyy-MM-dd'),
    end_date: format(rangeEnd, 'yyyy-MM-dd'),
  }), [rangeStart, rangeEnd]);
  const isCurrentPeriod = useMemo(
    () => getRange(period, new Date()).start.getTime() <= rangeStart.getTime(),
    [period, rangeStart]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('action') === 'new') {
      handleCreateNew();
    }
  }, [location.search]);

  const { data: expensesData, isLoading } = useQuery({
    queryKey: ['expenses', { type: 'expense', status: statusFilter, ...dateParams }],
    queryFn: () => expensesApi.getAll({
      type: 'expense',
      status: statusFilter || undefined,
      ...dateParams,
    }).then((res) => res.data),
  });

  const { data: summaryData } = useQuery({
    queryKey: ['expense-summary', period, dateParams],
    queryFn: () => expensesApi.getSummary({ period, ...dateParams }).then((res) => res.data),
  });

  const { data: timeseriesData } = useQuery({
    queryKey: ['expense-timeseries', period, dateParams],
    queryFn: () => expensesApi.getTimeseries({ period, ...dateParams }).then((res) => res.data),
  });

  const deleteMutation = useMutation({
    mutationFn: expensesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-summary'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success('Расход удалён');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Ошибка при удалении расхода'));
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: (id) => expensesApi.markPaid(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-summary'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success('Расход отмечен как оплаченный');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Ошибка при обновлении статуса расхода'));
    },
  });

  const importStatementMutation = useMutation({
    mutationFn: (file) => expensesApi.importBankStatement(file),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-summary'] });
      queryClient.invalidateQueries({ queryKey: ['expense-timeseries'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      const { results, summary } = res.data;
      setBulkResults({
        summary,
        results: results.map((r) => ({
          filename: `${r.date ? formatDate(r.date) : '—'} — ${r.counterparty || 'Без контрагента'}`,
          status: r.status,
          primary: r.excluded_reason || `${r.category || 'Прочее'} — ${formatCurrency(r.amount)}`,
          detail: r.purpose,
        })),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Не удалось обработать выписку'));
    },
  });

  const handleUploadStatement = (e) => {
    // Копируем в массив немедленно и сбрасываем value после — e.target.files
    // живая ссылка, которую сброс иначе очистит до того, как мутация успеет
    // её прочитать (тот же порядок, что уже задокументирован в Invoices.jsx).
    const files = Array.from(e.target.files || []);
    if (files.length) importStatementMutation.mutate(files[0]);
    e.target.value = '';
  };

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setIsDialogOpen(true);
  };

  const handleDelete = (expense) => {
    if (window.confirm(`Удалить расход на сумму ${formatCurrency(expense.amount)}?`)) {
      deleteMutation.mutate(expense.id);
    }
  };

  const handleMarkPaid = (expense) => {
    if (window.confirm(`Отметить расход на сумму ${formatCurrency(expense.amount)} как оплаченный?`)) {
      markPaidMutation.mutate(expense.id);
    }
  };

  const handleCreateNew = () => {
    setSelectedExpense(null);
    setIsDialogOpen(true);
  };

  const handlePrevPeriod = () => setAnchorDate((d) => PERIOD_CONFIG[period].shift(d, -1));
  const handleNextPeriod = () => setAnchorDate((d) => PERIOD_CONFIG[period].shift(d, 1));
  const handleToday = () => setAnchorDate(new Date());

  const columns = [
    {
      header: 'Категория',
      accessor: 'category',
      cell: (expense) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Receipt className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900 flex items-center gap-1.5">
              {expense.category || 'Без категории'}
              {expense.needs_review && (
                <span
                  title={expense.review_reason || 'Требует проверки'}
                  data-testid={`needs-review-expense-${expense.id}`}
                >
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                </span>
              )}
            </p>
            {expense.description && (
              <p className="text-xs text-gray-500 truncate max-w-[220px]">{expense.description}</p>
            )}
            {(expense.employee_name || expense.employee_position) && (
              <p className="text-xs text-gray-400 truncate max-w-[220px]">
                {[expense.employee_name, expense.employee_position].filter(Boolean).join(' — ')}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      header: 'Сумма',
      accessor: 'amount',
      cell: (expense) => (
        <span className="font-semibold text-gray-900" data-testid={`expense-amount-${expense.id}`}>
          {formatCurrency(expense.amount)}
        </span>
      ),
    },
    {
      header: 'Дата',
      accessor: 'date',
      cell: (expense) => (
        <span className="text-sm text-gray-600">
          {formatDate(expense.actual_date || expense.planned_date)}
        </span>
      ),
    },
    {
      header: 'Статус',
      accessor: 'status',
      cell: (expense) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(expense.status)}`}>
          {getStatusLabel('expense', expense.status)}
        </span>
      ),
    },
    {
      header: 'Действия',
      accessor: 'actions',
      cell: (expense) => (
        <div className="flex items-center space-x-2">
          {expense.status === 'pending' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleMarkPaid(expense);
              }}
              className="text-green-600 hover:text-green-900 p-1 rounded"
              disabled={markPaidMutation.isPending}
              data-testid={`mark-paid-expense-${expense.id}`}
              title="Отметить как оплаченный"
            >
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(expense);
            }}
            className="text-blue-600 hover:text-blue-900 p-1 rounded"
            data-testid={`edit-expense-${expense.id}`}
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(expense);
            }}
            className="text-red-600 hover:text-red-900 p-1 rounded"
            disabled={deleteMutation.isPending}
            data-testid={`delete-expense-${expense.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: '', label: 'Все статусы' },
    { value: 'pending', label: 'Ожидает оплаты' },
    { value: 'paid', label: 'Оплачено' },
  ];

  const periodOptions = [
    { value: 'week', label: 'Неделя' },
    { value: 'month', label: 'Месяц' },
    { value: 'quarter', label: 'Квартал' },
    { value: 'year', label: 'Год' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Расходы</h1>
        </div>
        <LoadingCard />
      </div>
    );
  }

  const netProfit = summaryData?.net_profit || 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Расходы</h1>
          <p className="text-gray-600 mt-1">Расходы компании и реальная прибыль</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            onChange={handleUploadStatement}
            className="hidden"
            data-testid="upload-statement-input"
          />
          {hasDocRecognition ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importStatementMutation.isPending}
              className="border border-purple-600 text-purple-600 px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-purple-50 transition-colors disabled:opacity-50"
              data-testid="upload-statement-btn"
            >
              <Upload className="w-5 h-5" />
              <span>{importStatementMutation.isPending ? 'Обрабатываем...' : 'Загрузить выписку'}</span>
            </button>
          ) : (
            <a
              href="/billing"
              className="border border-gray-300 text-gray-500 px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-50 transition-colors"
              data-testid="upload-statement-upsell"
            >
              <Upload className="w-5 h-5" />
              <span>Загрузить выписку (Professional+)</span>
            </a>
          )}
          <button
            onClick={handleCreateNew}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors"
            data-testid="create-expense-btn"
          >
            <Plus className="w-5 h-5" />
            <span>Добавить расход</span>
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevPeriod}
            className="p-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
            data-testid="prev-period-btn"
            title="Предыдущий период"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="min-w-[180px] text-center font-medium text-gray-900" data-testid="period-range-label">
            {formatRangeLabel(period, rangeStart, rangeEnd)}
          </span>
          <button
            onClick={handleNextPeriod}
            disabled={isCurrentPeriod}
            className="p-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            data-testid="next-period-btn"
            title="Следующий период"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {!isCurrentPeriod && (
            <button
              onClick={handleToday}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium ml-1"
              data-testid="today-period-btn"
            >
              Сегодня
            </button>
          )}
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="period-select"
        >
          {periodOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="expenses-chart">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Доходы, расходы и остаток</h3>
        {!timeseriesData ? (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={timeseriesData.points} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12 }}
                interval={Math.max(0, Math.ceil(timeseriesData.points.length / 10) - 1)}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
              <Legend />
              <Bar yAxisId="left" dataKey="actual_income" name="Факт доходов" fill="#3B82F6" />
              <Bar yAxisId="left" dataKey="actual_expense" name="Расходы" fill="#EF4444" />
              <Line yAxisId="right" type="monotone" dataKey="balance" name="Остаток" stroke="#16A34A" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <StatsCard
          title="План доходов"
          value={formatCurrency(summaryData?.planned_income || 0)}
          icon={TrendingUp}
        />
        <StatsCard
          title="Факт доходов"
          value={formatCurrency(summaryData?.actual_income || 0)}
          icon={TrendingUp}
        />
        <StatsCard
          title="Расходы"
          value={formatCurrency(summaryData?.actual_expense || 0)}
          subtitle={`План: ${formatCurrency(summaryData?.planned_expense || 0)}`}
          icon={TrendingDown}
        />
        <StatsCard
          title="Остаток на конец периода"
          value={formatCurrency(summaryData?.balance_at_period_end || 0)}
          icon={Wallet}
          className={(summaryData?.balance_at_period_end || 0) < 0 ? 'border-red-200 bg-red-50' : ''}
        />
        <StatsCard
          title="Прибыль"
          value={formatCurrency(netProfit)}
          icon={RussianRuble}
          className={netProfit < 0 ? 'border-red-200 bg-red-50' : ''}
        />
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="status-filter-select"
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <DataTable
          columns={columns}
          data={expensesData?.records || []}
          onRowClick={(expense) => handleEdit(expense)}
        />
      </div>

      <ExpenseDialog
        expense={selectedExpense}
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedExpense(null);
        }}
      />

      <UploadResultsDialog
        isOpen={!!bulkResults}
        onClose={() => setBulkResults(null)}
        title="Результаты импорта выписки"
        summary={bulkResults?.summary}
        results={bulkResults?.results}
      />
    </div>
  );
}
